import { useTranslation } from "react-i18next";
import { formatPrice } from "@nh/shared";
import type { ResolvedPrice } from "../shop.catalog";

export interface ProductCardPriceProps {
  price: ResolvedPrice | null;
  /** Locale de formato de números ("es-MX" / "en-US"). */
  numberLocale: string;
}

/**
 * El precio de una tarjeta: el tachado arriba, la cifra grande en medio,
 * y debajo la moneda con el porcentaje de rebaja — el mismo apilado de la
 * tienda de producción.
 *
 * ── El formato NO se hace aquí ───────────────────────────────────────
 * `formatPrice` vive en `@nh/shared` y es la única que sabe que los
 * precios son centavos enteros. Dividir entre 100 en un componente es
 * como se acaba con dos redondeos distintos en dos pantallas — y con un
 * total que no cuadra con lo que el cliente creyó pagar.
 *
 * ── El porcentaje se calcula, no se guarda ───────────────────────────
 * Sale de los dos precios que ya están a la vista. Un descuento guardado
 * aparte se desincroniza el día que alguien edita el precio y no el
 * porcentaje, y entonces la tarjeta anuncia un 30% que no es.
 *
 * ── Sin precio no se pinta un cero ───────────────────────────────────
 * "$0.00" se lee como gratis. Un producto sin precio capturado dice
 * "Precio a consultar", que es lo que de verdad ocurre.
 */
export function ProductCardPrice({ price, numberLocale }: ProductCardPriceProps) {
  const { t } = useTranslation();

  if (!price) {
    return (
      <p className="text-[13px] font-medium text-white drop-shadow-sm">
        {t("Precio a consultar")}
      </p>
    );
  }

  const discountPercent =
    price.compareAtCents !== undefined
      ? Math.round(((price.compareAtCents - price.cents) / price.compareAtCents) * 100)
      : 0;

  return (
    <div className="text-right">
      {price.compareAtCents !== undefined && (
        <p className="text-[13px] font-light tracking-tight text-white/75 line-through drop-shadow-sm">
          {formatPrice(price.compareAtCents, price.currency, numberLocale)}
        </p>
      )}

      <p className="text-xl font-light tracking-tight text-white drop-shadow-sm sm:text-2xl">
        {formatPrice(price.cents, price.currency, numberLocale)}
      </p>

      {/*
        Blanco al 80% y no al 70% como en producción: sobre el velo esta
        línea es la más pequeña de la tarjeta (10 px) y es la que lleva la
        moneda, que en una tienda con dos monedas no es decoración.
      */}
      <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/80 drop-shadow-sm">
        {price.currency}
        {discountPercent > 0 ? ` · −${discountPercent}%` : ""}
      </p>
    </div>
  );
}
